const getRevision = (template, platform) => Number(template?.[`${platform}_sync_revision`] ?? 0)
const getCheckedRevision = (template, platform) => Number(template?.[`${platform}_checked_revision`] ?? 0)
const getStatus = (template, platform) => String(template?.[`${platform}_check_status`] ?? 'stale')
const getSyncCount = (template, platform) => Number(template?.[`${platform}_sync_count`] ?? 0)
const getCandidateTotal = (template, platform) => Number(template?.[`${platform}_candidate_total`] ?? 0)
const getCheckedSoFar = (template, platform) => Number(template?.[`${platform}_checked_so_far`] ?? 0)
const getIssuedPassCount = template => (
  Number(template?.live_passes_issued ?? 0) + Number(template?.test_passes_issued ?? 0)
)

const isPlatformCurrent = (template, platform) => {
  const revision = getRevision(template, platform)
  const checkedRevision = getCheckedRevision(template, platform)
  const status = getStatus(template, platform)

  if (revision !== checkedRevision) return false
  if (status === 'ready') return true

  return revision === 0 && checkedRevision === 0
}

const platformNeedsCheck = (template, platform) => getRevision(template, platform) !== getCheckedRevision(template, platform)

const isPlatformCheckRunning = (template, platform) => {
  const status = getStatus(template, platform)
  return status === 'queued' || status === 'running'
}

const hasPendingTemplateChanges = template => (
  platformNeedsCheck(template, 'apple')
  || platformNeedsCheck(template, 'google')
)

const hasCurrentSyncAnswer = template => (
  isPlatformCurrent(template, 'apple')
  && isPlatformCurrent(template, 'google')
)

const getCurrentSyncCounts = template => {
  const apple = isPlatformCurrent(template, 'apple') ? getSyncCount(template, 'apple') : 0
  const google = isPlatformCurrent(template, 'google') ? getSyncCount(template, 'google') : 0
  const unique = hasCurrentSyncAnswer(template) ? Number(template?.unique_sync_count ?? 0) : 0
  const live = hasCurrentSyncAnswer(template)
    ? Number(template?.unique_live_sync_count ?? unique)
    : 0
  const test = hasCurrentSyncAnswer(template)
    ? Number(template?.unique_test_sync_count ?? Math.max(unique - live, 0))
    : 0

  return { apple, google, unique, live, test }
}

const hasSyncAttention = template => {
  if (getIssuedPassCount(template) === 0) return false
  const { unique } = getCurrentSyncCounts(template)

  return hasPendingTemplateChanges(template)
    || isPlatformCheckRunning(template, 'apple')
    || isPlatformCheckRunning(template, 'google')
    || unique > 0
}

const getSyncIndicator = template => {
  if (getIssuedPassCount(template) === 0) return null
  const { unique } = getCurrentSyncCounts(template)

  if (isPlatformCheckRunning(template, 'apple') || isPlatformCheckRunning(template, 'google')) {
    return {
      label: 'Checking',
      tone: 'info',
    }
  }

  if (hasPendingTemplateChanges(template)) {
    return {
      label: 'Check required',
      tone: 'warning',
    }
  }

  if (Boolean(template?.sync_execution_in_progress) && unique > 0) {
    return {
      label: 'Syncing',
      tone: 'info',
    }
  }

  if (unique > 0) {
    return {
      label: `${unique.toLocaleString()} to sync`,
      tone: 'success',
    }
  }

  return null
}

const getPlatformProgress = (template, platform) => {
  const total = getCandidateTotal(template, platform)
  const checked = Math.min(getCheckedSoFar(template, platform), total || getCheckedSoFar(template, platform))

  return {
    status: getStatus(template, platform),
    total,
    checked,
    current: isPlatformCurrent(template, platform),
    needsCheck: platformNeedsCheck(template, platform),
    running: isPlatformCheckRunning(template, platform),
    syncCount: getSyncCount(template, platform),
    percent: total > 0 ? Math.round((checked / total) * 100) : 0,
  }
}

export {
  getCurrentSyncCounts,
  getPlatformProgress,
  getSyncIndicator,
  getStatus,
  hasCurrentSyncAnswer,
  hasPendingTemplateChanges,
  hasSyncAttention,
  isPlatformCheckRunning,
  isPlatformCurrent,
  platformNeedsCheck,
}
